// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getSecondaryWidgetFields } from "@steiner/App/Store/Selectors/WidgetSelectors";
import translate from "@insite/client-framework/Translate";
import { getSettingsCollection } from "@steiner/App/Store/Selectors/SettingsSelectors";
import { getPageLinkByNodeId, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkModel, MappedLink } from "@steiner/Overrides/Widgets/Header/MainNavigation";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";

const getSecondaryMappedLinks = (state: ApplicationState, links: LinkModel[]) => {
    const compactHeaderLinks: MappedLink[] = [];
    for (const link of links) {
        const {
            fields: {
                destination,
                openInNewWindow,
                overriddenTitle,
                overrideDestination,
                numberOfColumns,
                maxDepth,
                title,
                menuVariant,
            },
        } = link;

        const { type, value } = destination;

        let mappedLink: MappedLink | undefined;

        if (type === "Url") {
            mappedLink = {
                url: value,
                title: overriddenTitle || overriddenTitle || title,
            };
        } else if (type === "Page") {
            const readonlyPageLink = getPageLinkByNodeId(state, value);
            if (readonlyPageLink) {
                mappedLink = { ...readonlyPageLink };
                if (overriddenTitle) {
                    mappedLink.title = overriddenTitle;
                }
            }
        }

        if (!mappedLink) {
            continue;
        }

        mappedLink.openInNewWindow = openInNewWindow;
        mappedLink.numberOfColumns = numberOfColumns;
        mappedLink.maxDepth = maxDepth;
        mappedLink.menuVariant = menuVariant;
        mappedLink.isBold = link.fields.isBold;
        mappedLink.type = "Link";
        mappedLink.excludeFromNavigation = false;
        mappedLink.children = undefined;

        if (overrideDestination?.value) {
            mappedLink.url =
                overrideDestination.type === "Page"
                    ? getPageLinkByNodeId(state, overrideDestination.value)?.url || ""
                    : overrideDestination.value;
        }

        compactHeaderLinks.push(mappedLink);
    }

    return compactHeaderLinks;
};

const getMenuCompactHeaderLinks = (state: ApplicationState, mainMappedLinks: MappedLink[]) => {
    const secondaryLinksFields = getSecondaryWidgetFields(state) || { primaryLinks: [], secondaryLinks: [] };
    const secondaryLinks: LinkModel[] = [
        ...secondaryLinksFields!.primaryLinks,
        ...secondaryLinksFields!.secondaryLinks,
        {
            fields: {
                title: translate("Contact Us"),
                destination: { type: "Url", value: getSettingsCollection(state).customSharedSettings.contactUsUrl },
            },
        },
        {
            fields: {
                title: translate("Quote"),
                destination: { type: "Page", value: getPageLinkByPageType(state, "RfqMyQuotesPage")?.id },
            },
        },
    ];
    let compactHeaderLinks: MappedLink[] = [...mainMappedLinks.map(link => ({ ...link, isBold: true }))];
    compactHeaderLinks = [...compactHeaderLinks, ...getSecondaryMappedLinks(state, secondaryLinks)];

    return compactHeaderLinks;
};

export default getMenuCompactHeaderLinks;
